import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Tooltip } from '@fluentui/react-components';
import { CopyRegular, CheckmarkRegular } from '@fluentui/react-icons';

const COPIED_FEEDBACK_MS = 1500;

export interface CopyIconButtonProps {
  /** Text to write to the clipboard. */
  value: string;
  /** Human-readable label used in aria attributes (e.g. "URL", "Callback URL"). */
  label: string;
  size?: 'small' | 'medium' | 'large';
  appearance?: 'secondary' | 'primary' | 'outline' | 'subtle' | 'transparent';
  className?: string;
}

/**
 * Icon button that copies `value` to the clipboard and flashes a checkmark +
 * "Copied!" tooltip for ~1.5s. Silently no-ops if the clipboard API is
 * unavailable (non-secure origin, denied permission) so the UI doesn't get
 * stuck in a half-state — callers can still expose `value` via a `title`
 * attribute for manual fallback copy.
 */
export const CopyIconButton = ({
  value,
  label,
  size,
  appearance = 'subtle',
  className,
}: CopyIconButtonProps) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  const handleCopy = useCallback(async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      return;
    }
    setCopied(true);
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      setCopied(false);
      timerRef.current = null;
    }, COPIED_FEEDBACK_MS);
  }, [value]);

  return (
    <Tooltip content={copied ? 'Copied!' : 'Copy'} relationship="label">
      <Button
        appearance={appearance}
        {...(size ? { size } : {})}
        {...(className ? { className } : {})}
        icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
        onClick={handleCopy}
        aria-label={copied ? `${label} copied` : `Copy ${label}`}
      />
    </Tooltip>
  );
};
